import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useEffect } from "react";

import { useAppStore } from "./useAppStore"; 
import { useUserStore } from './useUserStore';

import { getRandomColor, colorOptions, calculateNextButtonLabel } from '../utils/helpers';
import { useHover } from '../contexts/HoverContext'; // Import useHover

import { useNotification } from '../contexts/NotificationContext'; 
import { useTranslation } from 'react-i18next'; // Importing translation hook

export const useAppData = () => {
    const queryClient = useQueryClient();
    const { 
        handleHoverEnter, 
        handleHoverLeave, 
        hoveredButtonId,
        isDraggingWord,
        draggedWordPosition,
        stageRef
    } = useHover();

    const { 
        baseURL,
        wordListId, setWordListId, 
        soundboardId, setSoundboardId,
        stageWidth, setStageWidth,
        stageHeight, setStageHeight,
        buttonPopovers, setButtonPopovers,
        shapePopovers, setShapePopovers,
        selectedItem, setSelectedItem,
        wordListWidth, setWordListWidth,
        assignedWordTooltipVisibility, setAssignedWordTooltipVisibility,
        isPendingAssignWord, setIsPendingAssignWord,
        isPendingDeleteGroup, setIsPendingDeleteGroup,
        lastUsedShapeColor, setLastUsedShapeColor,
        lastUsedButtonColor, setLastUsedButtonColor
    } = useAppStore();

    const { 
        user, setUser,
    } = useUserStore();

    const { t } = useTranslation(); // Hook to access translation
    const { showNotification } = useNotification();    

    /********** WORDLIST**********/

    // ✅ Fetch WordList from API
    const {
        data: wordList, 
        isPending: isWordListPending, 
        isError: isWordListError, 
        refetch: wordListRefetch } = useQuery({
        queryKey: ["wordList", wordListId],
        queryFn: async () => {
            const response = await axios.get(`${baseURL}/api/wordLists/${wordListId}/getWordList`);
            const wordGroups = response.data.wordGroups; 

            return wordGroups;
        },
        onError: (error) => {
            showNotification({
              notification: "Failed to retrieve the word list.",
              type: "error",
              extra: { message: error.message, wordListId },
              isException: true,
              actions: [{ label: "Refresh", onClick: () => window.location.reload() }],
            });
        },
        enabled: !!wordListId, // Only fetch if wordListId is set
        staleTime: 1000 * 60 * 5, // Keep data fresh for 5 minutes
    });


    /* ✅  wordGroupid: If an action pertains to entire wordList, this can be '', but this is not implemented in backend and no action affects entire wordList
      wordId: If action is on wordGroup level, wordId is ''
      field: the field to be changed, allowed groupName, text, buttonLabel, color
      newValue: What the new value should be*/
    const updateWordList = useMutation({
        mutationFn: async ({ wordGroupId, wordId, updates }) => {
            return axios.put(`${baseURL}/api/wordLists/wordList/${wordListId}`, {
            wordGroupId,
            wordId,
            updates, // array of { field, newValue }
            });
        },
        onMutate: async ({ wordGroupId, wordId, updates }) => {
            await queryClient.cancelQueries(["wordList", wordListId]);
            const previousWordList = queryClient.getQueryData(["wordList", wordListId]);
        
            queryClient.setQueryData(["wordList", wordListId], (old) => {
            if (!old) return old;
        
            return old.map((group) => {
                if (group._id !== wordGroupId) return group;
        
                if (wordId) {
                return {
                    ...group,
                    wordItems: group.wordItems.map((wordItem) =>
                    wordItem._id === wordId
                        ? {
                            ...wordItem,
                            ...Object.fromEntries(
                            updates.map(({ field, newValue }) => [field, newValue])
                            ),
                        }
                        : wordItem
                    ),
                };
                } else {
                // Group-level update (e.g., groupName)
                return {
                    ...group,
                    ...Object.fromEntries(
                    updates.map(({ field, newValue }) => [field, newValue])
                    ),
                };
                }
            });
            });
        
            return { previousWordList };
        },
        onError: (error, variables, context) => {
            queryClient.setQueryData(["wordList", wordListId], context.previousWordList);
        
            for (const { field, newValue } of variables.updates || []) {
            showNotification({
                notification: t("errors.snackbar.wordListNotUpdated"),
                type: "error",
                extra: {
                message: "Failed to update word list on server",
                wordListId,
                wordGroupId: variables.wordGroupId,
                wordId: variables.wordId,
                field,
                newValue,
                },
                error,
                isException: true,
            });
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries(["wordList", wordListId]);
        },
    });

    // ✅ Reorder WordList
    const updateWordListOnReorder = useMutation({
        mutationFn: async ({ source, destination }) => {
            return axios.put(`${baseURL}/api/wordLists/wordList/${wordListId}/reorderItems`, {
                source,
                destination,
            });
        },
        onMutate: async ({ source, destination }) => {
            await queryClient.cancelQueries(["wordList", wordListId]);
            
            const previousWordList = queryClient.getQueryData(["wordList", wordListId]);
            if (!previousWordList) return { previousWordList };
            
            const newWordList = JSON.parse(JSON.stringify(previousWordList)); // Deep clone
            
            const sourceGroupIndex = newWordList.findIndex(group => group._id === source.droppableId);
            const destinationGroupIndex = newWordList.findIndex(group => group._id === destination.droppableId);
            
            if (sourceGroupIndex === -1 || destinationGroupIndex === -1) {
                return { previousWordList };
            }
            
            const sourceGroup = newWordList[sourceGroupIndex];
            const destinationGroup = newWordList[destinationGroupIndex];
            
            const [movedItem] = sourceGroup.wordItems.splice(source.index, 1);
            
            if (!movedItem) {
                return { previousWordList };
            }
            
            destinationGroup.wordItems.splice(destination.index, 0, movedItem);
            
            queryClient.setQueryData(["wordList", wordListId], newWordList);
            
            return { previousWordList };
        },
        onError: (error, _variables, context) => {
            queryClient.setQueryData(["wordList", wordListId], context.previousWordList);

            showNotification({
                notification: t("errors.snackbar.wordListNotReordered"),
                type: "error",
                extra: {
                message: "Failed to reorder word list on server",
                wordListId,
                source: _variables.source,
                destination: _variables.destination,
                },
                error,
                isException: true,
            });
        },
        onSettled: () => {
        queryClient.invalidateQueries(["wordList", wordListId]);
        },
    });

    // ✅ Add new empty group
    const addGroup = useMutation({
        mutationFn: async (groupName) => {
            const response = await axios.post(`${baseURL}/api/wordLists/wordList/${wordListId}/wordGroups`, { groupName });
            return response.data; // Return the full group object with real MongoDB _id, not the entire wordList is returned
        },
        onMutate: async (groupName) => {
            await queryClient.cancelQueries(["wordList", wordListId]);

            const previousWordList = queryClient.getQueryData(["wordList", wordListId]);

            const tempId = Date.now().toString(); // Temporary ID for UI update


            queryClient.setQueryData(["wordList", wordListId], (old) => {
                if (!old) return old;
                return [{ _id: tempId, groupName, wordItems: [] }, ...old];
            });

            return { previousWordList, tempId };
        },
        onSuccess: (data, groupName, context) => {
            queryClient.setQueryData(["wordList", wordListId], (old) => {
                if (!old) return old;
                return old.map((group) =>
                group._id === context.tempId ? { ...group, _id: data._id } : group
                );
            });
        },
        onError: (error, groupName, context) => {
            queryClient.setQueryData(["wordList", wordListId], context.previousWordList);

            showNotification({
                notification: t("errors.snackbar.wordListGroupNotAdded"),
                type: "error",
                extra: {
                message: "Failed to add word group",
                wordListId,
                groupName,
                },
                error,
                isException: true,
            });
        },
        onSettled: () => {
            queryClient.invalidateQueries(["wordList", wordListId]);
        },
    });

    // ✅ Delete Group (Handles Button Unassignment First)
    const deleteGroup = useMutation({
        mutationFn: async (groupId) => {
            const group = queryClient.getQueryData(["wordList", wordListId])?.find(g => g._id === groupId);
            if (!group) throw new Error("Group not found");

            const wordIds = group.wordItems.map(word => word._id);

            // Find buttons that are assigned to the words in this group
            const buttonsToUnassign = shapes
                .flatMap(shape =>
                    shape.buttons
                        .filter(button => wordIds.includes(button.wordId))
                        .map(button => ({
                        ...button,
                        shapeId: shape._id,
                        shapeColor: shape.color || "transparent",
                        }))
                );


            await Promise.all(buttonsToUnassign.map(button =>
                axios.patch(`${baseURL}/api/soundboards/soundboard/${soundboardId}/button/${button._id}/updateButton`, {
                    buttonId: button._id,
                    updates: [
                        { field: "wordId", value: null },
                        { field: "buttonLabel", value: null },
                        { field: "color", value: null }
                    ]
                })
            ));

            // Delete the group
            await axios.delete(`${baseURL}/api/wordLists/${wordListId}/${groupId}`);

        },
        /*onMutate: async (groupId) => {
            queryClient.cancelQueries(["wordList", wordListId]);
            queryClient.cancelQueries(["soundboard", soundboardId]);

            // Remove cached results entirely so stale UI can't come back
            queryClient.removeQueries(["wordList", wordListId]);
            queryClient.removeQueries(["soundboard", soundboardId]);
        
            const previousWordList = queryClient.getQueryData(["wordList", wordListId]);
            const previousSoundboard = queryClient.getQueryData(["soundboard", soundboardId]);
        
            const group = previousWordList?.find(g => g._id === groupId);
            const wordIds = group?.wordItems.map(word => word._id) || [];

            queryClient.setQueryData(["wordList", wordListId], (old) =>
            old ? old.filter(group => group._id !== groupId) : old
            );

            queryClient.setQueryData(["soundboard", soundboardId], (old) => {
            if (!old) return old;
            return {
                ...old,
                shapes: old.shapes.map(shape => ({
                ...shape,
                buttons: shape.buttons.map(button =>
                    wordIds.includes(button.wordId)
                    ? { ...button, wordId: null, buttonLabel: null, color: shape.color || '' }
                    : button
                ),
                })),
            };
            });

            return { previousWordList, previousSoundboard };
            
        },*/
        onError: (error, groupId, context) => {
            showNotification({
                notification: t("errors.snackbar.wordListGroupNotDeleted"),
                type: "error",
                extra: {
                message: "Failed to delete group from server",
                wordListId,
                groupId,
                },
                error,
                isException: true,
            });
        },
        onSettled: () => {
            setIsPendingDeleteGroup(false);

            // ✅ Re-enable refetching now that backend is updated
            queryClient.setQueryDefaults(["wordList", wordListId], { enabled: true });
            queryClient.setQueryDefaults(["soundboard", soundboardId], { enabled: true });

            queryClient.invalidateQueries(["wordList", wordListId]);
            queryClient.invalidateQueries(["soundboard", soundboardId ]);
        },
    });

    // ✅ Add word to word list
    const addWordItem = useMutation({
        mutationFn: async ({ wordGroupId, newWord }) => {
            const response = await axios.post(
            `${baseURL}/api/wordLists/wordList/${wordListId}/wordGroups/${wordGroupId}/addWordItem`,
            { newWordItemText: newWord }
            );
            return response.data; 
        },
        onMutate: async ({ wordGroupId, newWord }) => {
            await queryClient.cancelQueries(["wordList", wordListId]);
        
            const previousWordList = queryClient.getQueryData(["wordList", wordListId]);
        
            const tempId = Date.now().toString();
        
            queryClient.setQueryData(["wordList", wordListId], (old) => {
            if (!old) return old;
        
            return old.map((group) =>
                group._id === wordGroupId
                ? {
                    ...group,
                    wordItems: [...group.wordItems, { _id: tempId, text: newWord }],
                    }
                : group
            );
            });
        
            return { previousWordList, tempId };
        },
        onError: (error, { wordGroupId, newWord }, context) => {
            queryClient.setQueryData(["wordList", wordListId], context.previousWordList);
        
            showNotification({
            notification: t("errors.snackbar.wordListWordNotAdded"),
            type: "error",
            extra: {
                message: "Failed to add word on server",
                wordListId,
                wordGroupId,
                newWord,
            },
            error,
            isException: true,
            });
        },
        onSuccess: (data, { wordGroupId }, context) => {
            queryClient.setQueryData(["wordList", wordListId], (old) =>
            old.map((group) =>
                group._id === wordGroupId
                ? {
                    ...group,
                    wordItems: group.wordItems.map((wordItem) =>
                        wordItem._id === context.tempId ? { ...wordItem, _id: data._id } : wordItem
                    ),
                }
                : group
            ));
        },
        onSettled: () => {
            queryClient.invalidateQueries(["wordList", wordListId]);
        },
    });

    // ✅ Delete a single word from word list
    const deleteWord = useMutation({
        mutationFn: async ({ wordGroupId, wordId }) => {
            await axios.delete(`${baseURL}/api/wordLists/wordList/${wordListId}/wordGroup/${wordGroupId}/word/${wordId}/deleteWord`);
        },
        onMutate: async ({ wordGroupId, wordId }) => {
            await queryClient.cancelQueries(["wordList", wordListId]);
        
            const previousWordList = queryClient.getQueryData(["wordList", wordListId]);
        
            // Remove the word from the cached word list
            queryClient.setQueryData(["wordList", wordListId], (old) => {
            if (!old) return old;
        
            return old.map((group) =>
                group._id === wordGroupId
                ? {
                    ...group,
                    wordItems: group.wordItems.filter((word) => word._id !== wordId), 
                    }
                : group
            );
            });
        
            return { previousWordList };
        },
        onSuccess: async (_, { wordGroupId, wordId }) => {
            // Find associated buttons
            const soundboard = queryClient.getQueryData(["soundboard", soundboardId]);
            const buttonsToUpdate = soundboard?.shapes.flatMap(shape =>
                shape.buttons.filter(button => button.wordId === wordId)
            ) || [];
    
            // Reset button color to transparent in backend
            if (buttonsToUpdate.length > 0) {
                await Promise.all(
                  buttonsToUpdate.map(button =>
                    updateButton.mutateAsync({
                      buttonId: button._id,
                      updates: [{ field: "color", value: null }]
                    })
                  )
                );
              }
    
            // 🔹 Reset the hover state to prevent the ghost hover effect
            handleHoverLeave();  
    
            // Refresh UI state
            queryClient.invalidateQueries(["soundboard", soundboardId]);
        },
        onError: (error, { wordGroupId, wordId }, context) => {
            queryClient.setQueryData(["wordList", wordListId], context.previousWordList);
        
            showNotification({
            notification: t("errors.snackbar.wordListWordNotDeleted"),
            type: "error",
            extra: {
                message: "Failed to delete word from server",
                wordListId,
                wordGroupId,
                wordId,
            },
            error,
            isException: true,
            });
        },
        onSettled: () => {
            queryClient.invalidateQueries(["wordList", wordListId]);
        },
        });

        /********** SOUNDBOARD **********/
        // ✅ Fetch Soundboard from API
        const {
        data: soundboard,
        isPending: isSoundboardPending,
        isError: isSoundboardError,
        refetch: soundboardRefetch,
        } = useQuery({
        queryKey: ["soundboard", soundboardId], 
        queryFn: async () => {
            const response = await axios.get(`${baseURL}/api/soundboards/soundboard/${soundboardId}`);
            return response.data;
        },
        enabled: !!soundboardId,
        staleTime: 1000 * 60 * 5,
        onError: (error) => {
            showNotification({
                notification: t("errors.snackbar.soundboardNotRetrieved"),
                type: "error",
                extra: { message: "Failed to get soundboard from server", soundboardId },
                error,
                isException: true,
            });
        },
    });

        
    // ✅ Extract `shapes` from `soundboard` so UI can still use `shapes`
    let shapes = soundboard?.shapes || [];
    
    // ✅ Adding a new shape to soundboard
    const addShape = useMutation({
        mutationFn: async (newShape) => {

          const soundboardId = useAppStore.getState().soundboardId; // to prepare for supporting multiple soundboards and dynamically updating to currentSoundboardId

            // Remove temp _id from buttons so Mongo can assign real ObjectIds
            const cleanButtons = newShape.buttons.map(({ _id, ...rest }) => rest);

            const { _id, ...restShape } = newShape;

            const cleanShape = {
            ...restShape,
            buttons: cleanButtons,
            };

          const response = await axios.post(
            `${baseURL}/api/soundboards/soundboard/${soundboardId}/shape`,
            cleanShape
          );
          return response.data; // Contains the newly created shape only
        },
        onMutate: async (newShapeData) => {
            await queryClient.cancelQueries(["soundboard", soundboardId]);
        
            const previousSoundboard = queryClient.getQueryData(["soundboard", soundboardId]);
            if (!previousSoundboard) return { previousSoundboard };
        
            const tempId = `temp-${Date.now()}`; // Generate a temporary client-only ID
        
            const optimisticShape = { ...newShapeData, _id: tempId };
        
            queryClient.setQueryData(["soundboard", soundboardId], (old) => ({
                ...old,
                shapes: [...old.shapes, optimisticShape]
            }));
        
            return { previousSoundboard, tempId };
        },
        onError: (error, newShape, context) => {
          const soundboardId = useAppStore.getState().soundboardId;
          queryClient.setQueryData(["soundboard", soundboardId], context.previousSoundboard);
      
          showNotification({
            notification: t("errors.snackbar.SoundboardShapeNotAdded"),
            type: "error",
            extra: { message: "Failed to add shape", soundboardId, newShape },
            error,
            isException: true,
          });
        },
        onSuccess: (serverShape, _, context) => {
            queryClient.setQueryData(["soundboard", soundboardId], (old) => {
                if (!old) return;
                return {
                ...old,
                shapes: old.shapes.map(shape =>
                    shape._id === context.tempId ? serverShape : shape
                )
                };
            });
        },
        onSettled: () => {
          const soundboardId = useAppStore.getState().soundboardId;
          queryClient.invalidateQueries(["soundboard", soundboardId]);
        },
      });

      // ✅ Update shape with possible fields:'color', 'x', 'y', 'radius', 'rotation', 'buttonLabel'];
      const updateShape = useMutation({
        mutationFn: async ({ shapeId, updates }) => {
          const soundboardId = useAppStore.getState().soundboardId; 
      
          const response = await axios.patch(
            `${baseURL}/api/soundboards/soundboard/${soundboardId}/shape/${shapeId}`,
            { updates } // [{ field, value }, ...]
          );
      
          return response.data; // Returns the updated shape
        },
        onMutate: async ({ shapeId, updates }) => {
          const soundboardId = useAppStore.getState().soundboardId;
          await queryClient.cancelQueries(["soundboard", soundboardId]);
      
          const previousSoundboard = queryClient.getQueryData(["soundboard", soundboardId]);
      
          queryClient.setQueryData(["soundboard", soundboardId], (old) => {
            if (!old) return old;
      
            return {
                ...old,
                shapes: old.shapes.map((shape) =>
                shape._id === shapeId
                    ? {
                        ...shape,
                        ...Object.fromEntries(updates.map(({ field, value }) => [field, value])),
                    }
                    : shape
                ),
            };
        });

          return { previousSoundboard };
        },      
        onSuccess: (updatedShape) => {
          const soundboardId = useAppStore.getState().soundboardId;
          queryClient.setQueryData(["soundboard", soundboardId], (old) =>
            old
              ? {
                  ...old,
                  shapes: old.shapes.map((shape) =>
                    shape._id === updatedShape._id ? updatedShape : shape
                  ),
                }
              : old
          );
        },
        onError: (_error, variables, context) => {
          const soundboardId = useAppStore.getState().soundboardId;
          queryClient.setQueryData(["soundboard", soundboardId], context.previousSoundboard);
      
          for (const { field, value } of variables.updates) {
            showNotification({
              notification: t("errors.snackbar.SoundboardShapeNotUpdated"),
              type: "error",
              extra: {
                message: "Error updating shape (front)",
                soundboardId,
                shapeId: variables.shapeId,
                field,
                value,
              },
              isException: true,
            });
          }
        },
        onSettled: () => {
          const soundboardId = useAppStore.getState().soundboardId;
          queryClient.invalidateQueries(["soundboard", soundboardId]);
        },
      });
      
      // ✅ Update button with possible fields: 'wordId','color', 'x', 'y', 'radius', 'rotation', 'buttonLabel'
      const updateButton = useMutation({
        mutationFn: async ({ buttonId, updates }) => {
          const soundboardId = useAppStore.getState().soundboardId; 

          const response = await axios.patch(
            `${baseURL}/api/soundboards/soundboard/${soundboardId}/button/${buttonId}/updateButton`,
            { updates } // Send the array of field-value pairs
          );
      
          return response.data; // Contains the updated button only, no shape
        },
        onMutate: async ({ buttonId, updates }) => {
          const soundboardId = useAppStore.getState().soundboardId;
          await queryClient.cancelQueries(["soundboard", soundboardId]);
      
          const previousSoundboard = queryClient.getQueryData(["soundboard", soundboardId]);
      
          queryClient.setQueryData(["soundboard", soundboardId], (old) => {
            if (!old) return old;
      
            return {
                ...old,
                shapes: old.shapes.map((shape) => ({
                    ...shape,
                    buttons: shape.buttons.map((button) =>
                    button._id === buttonId
                        ? {
                            ...button,
                            ...Object.fromEntries(updates.map(({ field, value }) => [field, value])),
                        }
                        : button
                    ),
                })),
                };
            });
      
          return { previousSoundboard };
        },
        onSuccess: (updatedButton) => {
          const soundboardId = useAppStore.getState().soundboardId;
          queryClient.setQueryData(["soundboard", soundboardId], (old) =>
            old
              ? {
                  ...old,
                  shapes: old.shapes.map((shape) => ({
                    ...shape,
                    buttons: shape.buttons.map((button) =>
                      button._id === updatedButton._id ? updatedButton : button
                    ),
                  })),
                }
              : old
          );
        },
        onError: (_error, variables, context) => {
          const soundboardId = useAppStore.getState().soundboardId;
          queryClient.setQueryData(["soundboard", soundboardId], context.previousSoundboard);
      
            for (const { field, value } of variables.updates) {
                showNotification({
                notification: t("errors.snackbar.SoundboardButtonNotUpdated"),
                type: "error",
                extra: {
                    message: "Error updating button (front)",
                    soundboardId,
                    buttonId: variables.buttonId,
                    field,
                    value,
                },
                isException: true,
                });
            }
        },
        onSettled: () => {
          const soundboardId = useAppStore.getState().soundboardId;
          queryClient.invalidateQueries(["soundboard", soundboardId]);
        },
      });
      
      // ✅ Handles both unassign in soundboard and wordlist in 2 separate calls. Since both needs to happen together, they are in one useMutation instead of chaining.
      const unassignButton = useMutation({
        mutationFn: async ({ shapeId, buttonIds }) => {
            const soundboardId = useAppStore.getState().soundboardId;
            const wordListId = useAppStore.getState().wordListId;

            const validButtonIds = Array.isArray(buttonIds) ? buttonIds : [buttonIds];  

            // Perform both API calls simultaneously
            const [responseWordList, responseSoundboard] = await Promise.all([
                axios.patch(`${baseURL}/api/wordLists/wordList/${wordListId}/word/unassignButtonWordList`, { buttonIds: validButtonIds }),
                axios.patch(`${baseURL}/api/soundboards/soundboard/${soundboardId}/shape/${shapeId}/button/unassignButtonSoundboard`, { buttonIds: validButtonIds }),
            ]);

            return {
                updatedWordList: responseWordList.data,
                updatedSoundboard: responseSoundboard.data,
            };
        },
        onMutate: async ({ shapeId, buttonIds }) => {
            const soundboardId = useAppStore.getState().soundboardId;
            const wordListId = useAppStore.getState().wordListId;
    
            await queryClient.cancelQueries(["wordList", wordListId]);
            await queryClient.cancelQueries(["soundboard", soundboardId]);
    
            const previousWordList = queryClient.getQueryData(["wordList", wordListId]);
            const previousSoundboard = queryClient.getQueryData(["soundboard", soundboardId]);
    
            // Optimistically update the UI
            queryClient.setQueryData(["wordList", wordListId], (old) => {
                if (!old) return old;
                return old.map((group) => ({
                    ...group,
                    wordItems: group.wordItems.map((word) =>
                        buttonIds.includes(word.buttonId) ? { ...word, buttonId: 0 } : word
                    ),
                }));
            });
    
            queryClient.setQueryData(["soundboard", soundboardId], (old) => {
                if (!old) return old;
                return {
                    ...old,
                    shapes: old.shapes.map((shape) =>
                        shape._id === shapeId
                            ? {
                                  ...shape,
                                  buttons: shape.buttons.map((button) =>
                                      buttonIds.includes(button._id)
                                          ? { ...button, wordId: null, buttonLabel: null, color: shape.color || "" }
                                          : button
                                  ),
                              }
                            : shape
                    ),
                };
            });
    
            return { previousWordList, previousSoundboard };
        },
        onSuccess: (data) => {
            queryClient.setQueryData(["wordList", data.updatedWordList._id], data.updatedWordList);
            queryClient.setQueryData(["soundboard", data.updatedSoundboard._id], data.updatedSoundboard);
        },
        onError: (error, variables, context) => {
            const soundboardId = useAppStore.getState().soundboardId;
            const wordListId = useAppStore.getState().wordListId;
    
            queryClient.setQueryData(["wordList", wordListId], context.previousWordList);
            queryClient.setQueryData(["soundboard", soundboardId], context.previousSoundboard);
    
            showNotification({
                notification: t("errors.snackbar.SoundboardButtonNotUnassigned"),
                type: "error",
                extra: {
                    message: "Error unassigning button",
                    soundboardId,
                    shapeId: variables.shapeId,
                    buttonIds: variables.buttonIds,
                },
                error,
                isException: true,
            });
        },
        onSettled: () => {
            const soundboardId = useAppStore.getState().soundboardId;
            const wordListId = useAppStore.getState().wordListId;
            queryClient.invalidateQueries(["wordList", wordListId]);
            queryClient.invalidateQueries(["soundboard", soundboardId]);
        },
    });

    // ✅ Delete shape and handles unassign buttons in backend
    const deleteShape = useMutation({
        mutationFn: async (shapeId) => {
            const soundboardId = useAppStore.getState().soundboardId;
    
            // Delete shape (backend already handles unassigning buttons)
            const response = await axios.delete(
                `${baseURL}/api/soundboards/soundboard/${soundboardId}/shape/${shapeId}`
            );
    
            return response.data; // Expect the updated soundboard
        },
        onMutate: async (shapeId) => {
            const soundboardId = useAppStore.getState().soundboardId;
            await queryClient.cancelQueries(["soundboard", soundboardId]);
            await queryClient.cancelQueries(["wordList", wordListId]); 
        
            const previousSoundboard = queryClient.getQueryData(["soundboard", soundboardId]);
            const previousWordList = queryClient.getQueryData(["wordList", wordListId]); 
        
            // Optimistically remove the shape
            queryClient.setQueryData(["soundboard", soundboardId], (old) => {
                if (!old) return old;
                return {
                    ...old,
                    shapes: old.shapes.filter(shape => shape._id !== shapeId),
                };
            });
        
            return { previousSoundboard, previousWordList }; 
        },
        onError: (_error, _variables, context) => {
            const soundboardId = useAppStore.getState().soundboardId;
            queryClient.setQueryData(["soundboard", soundboardId], context.previousSoundboard);
            queryClient.setQueryData(["wordList", wordListId], context.previousWordList); 
        
            showNotification({
                notification: t("errors.snackbar.SoundboardShapeNotDeleted"),
                type: "error",
                extra: { soundboardId },
                isException: true,
            });
        },
        onSuccess: (updatedSoundboard) => {
            const soundboardId = useAppStore.getState().soundboardId;
            queryClient.setQueryData(["soundboard", soundboardId], updatedSoundboard);
            queryClient.invalidateQueries(["wordList", wordListId]);
        },
        onSettled: () => {
            const soundboardId = useAppStore.getState().soundboardId;
            queryClient.invalidateQueries(["soundboard", soundboardId]);
            queryClient.invalidateQueries(["wordList", wordListId]); 
        },
    });

    // ✅ AssignButton. Assigns a word to button when an option is selected in buttonPopover, or a drag and drop happened
    const handleOptionChange = useMutation({
        mutationFn: async ({buttonId, newValue, nextLabel, newColor}) => {      

            if (!newValue) return;

            /*const soundboardId = useAppStore.getState().soundboardId;
            const wordListId = useAppStore.getState().wordListId;
            const selectedItem = useAppStore.getState().selectedItem;*/
            
            const { shapes } = queryClient.getQueryData(["soundboard", soundboardId]) || {};
            const wordList = queryClient.getQueryData(["wordList", wordListId]);
    
            // Find if the new word is already assigned to another button, but excluding current newly assigning button
            const oldButton = shapes?.flatMap(shape => shape.buttons).find(
                (button) =>
                  button.wordId === newValue._id && button._id !== buttonId
            );

            // If found, unassign it from the old button before assigning it to the new one
            if (oldButton) {
                const oldShape = shapes.find(shape => shape.buttons.some(button => button._id === oldButton._id));
                const oldButtonShapeId = oldShape?._id;

                if (oldButtonShapeId) {
                    if (oldButton._id === buttonId) {
                        return;
                    } else {
                        await unassignButton.mutateAsync({ shapeId: oldButtonShapeId, buttonIds: [oldButton._id] });
                    }
                }
            }
                /*const currentWordItem = useAppStore.getState().buttonPopovers[buttonId]?.selectedOption;
                
                // If a different word is currently assigned, unassign it first
            if (currentWordItem && currentWordItem._id !== newValue._id) {
                const shapeId = selectedItem?.shapeId;
                if (shapeId) {
                    await unassignButton.mutateAsync({ shapeId, buttonIds: [buttonId] });
                }
            }*/


            const button = shapes?.flatMap(shape => shape.buttons).find(btn => btn._id === buttonId);
            const currentWordId = button?.wordId;

            if (currentWordId && currentWordId !== newValue.buttonId) {
                const shape = shapes.find(s => s.buttons.some(b => b._id === buttonId));
                const shapeId = shape?._id;

                if (shapeId) {
                    await unassignButton.mutateAsync({ shapeId, buttonIds: [buttonId] });
                }
            }
            
            // Assign the new word
            await Promise.all([
                updateButton.mutateAsync({
                  buttonId,
                  updates: [
                    { field: "wordId", value: newValue._id },
                    { field: "buttonLabel", value: nextLabel },
                    { field: "color", value: newColor }
                  ]
                }),
                updateWordList.mutateAsync({
                    wordId: newValue._id,
                    updates: [
                      { field: "buttonId", newValue: buttonId },
                      { field: "color", newValue: newColor }
                    ]
                })
              ]);
    
            return { updatedSoundboard: queryClient.getQueryData(["soundboard", soundboardId]), updatedWordList: queryClient.getQueryData(["wordList", wordListId]) };
        },
        onMutate: async ({ buttonId, newValue }) => {
            setIsPendingAssignWord(true);

            const soundboardId = useAppStore.getState().soundboardId;
            const wordListId = useAppStore.getState().wordListId;
            await queryClient.cancelQueries(["soundboard", soundboardId]);
    
            const previousSoundboard = queryClient.getQueryData(["soundboard", soundboardId]);
            const previousWordList = queryClient.getQueryData(["wordList", wordListId]);
    
            const nextLabel = calculateNextButtonLabel(previousSoundboard?.shapes);
            const newColor = getRandomColor();
          
            queryClient.setQueryData(["soundboard", soundboardId], (old) => {
              if (!old) return old;
              return {
                ...old,
                shapes: old.shapes.map(shape => ({
                  ...shape,
                  buttons: shape.buttons.map(button =>
                    button._id === buttonId
                      ? { ...button, wordId: newValue._id, buttonLabel: nextLabel, color: newColor }
                      : button
                  ),
                })),
              };
            });
          
            return { previousSoundboard, previousWordList };
        },
        onError: (_error, _variables, context) => {
            const soundboardId = useAppStore.getState().soundboardId;
            queryClient.setQueryData(["soundboard", soundboardId], context.previousSoundboard);
            queryClient.setQueryData(["wordList", context.wordListId], context.previousWordList);
    
            showNotification({
                notification: t("errors.snackbar.SoundboardButtonNotUpdated"),
                type: "error",
                isException: true,
            });
        },
        onSuccess: (_data, { buttonId, newValue }) => {
            const wordList = queryClient.getQueryData(["wordList", useAppStore.getState().wordListId]);
    
            const updatedSelectedOption = wordList
                ?.flatMap(group => group.wordItems)
                .find(wordItem => wordItem._id === newValue._id);
    
            setButtonPopovers(prev => ({
                ...prev,
                [buttonId]: {
                    selectedOption: updatedSelectedOption,
                    inputValue: updatedSelectedOption ? updatedSelectedOption.text : '',
                    open: false
                }
            }));
        },
        onSettled: () => {
            setIsPendingAssignWord(false);
          },        
    });

    // ✅ Update for tool visibility preference
    const handleAssignedWordTooltipVisibility = useMutation({
        mutationFn: async (newVisibility) => {
            const soundboardId = useAppStore.getState().soundboardId;
            const response = await axios.patch(`${baseURL}/api/soundboards/soundboard/${soundboardId}/updateTooltipVisibility`, {
                assignedWordTooltipVisibility: newVisibility
            });
    
            return response.data.assignedWordTooltipVisibility;
        },
        onMutate: async (newVisibility) => {
            const soundboardId = useAppStore.getState().soundboardId;
            useAppStore.setState({ assignedWordTooltipVisibility: newVisibility });

            await queryClient.cancelQueries(["soundboard", soundboardId]);
    
            const previousSoundboard = queryClient.getQueryData(["soundboard", soundboardId]);
    
            queryClient.setQueryData(["soundboard", soundboardId], (old) => {
                if (!old) return old;
                return {
                    ...old,
                    assignedWordTooltipVisibility: newVisibility,
                };
            });

            return { previousSoundboard };
        },
        onError: (_error, _variables, context) => {
            const soundboardId = useAppStore.getState().soundboardId;
            queryClient.setQueryData(["soundboard", soundboardId], context.previousSoundboard);
    
            showNotification({
                notification: t("errors.snackbar.SoundboardTooltipNotUpdated"),
                type: "error",
                extra: {
                    message: "Failed to update tooltip visibility on the server",
                    soundboardId,
                    newVisibility: context.previousSoundboard?.assignedWordTooltipVisibility || "default",
                },
                isException: true,
            });
        },
        onSuccess: (newVisibility) => {
            const soundboardId = useAppStore.getState().soundboardId;

            useAppStore.setState({ assignedWordTooltipVisibility: newVisibility });

            queryClient.setQueryData(["soundboard", soundboardId], (old) => ({
                ...old,
                assignedWordTooltipVisibility: newVisibility,
            }));
        },
        onSettled: () => {
            const soundboardId = useAppStore.getState().soundboardId;
            queryClient.invalidateQueries(["soundboard", soundboardId]);
        },
    });

    /*useEffect(() => {
        if (soundboard?.assignedWordTooltipVisibility) {
            setAssignedWordTooltipVisibility(soundboard.assignedWordTooltipVisibility);
        }
    }, [soundboard?.assignedWordTooltipVisibility, setAssignedWordTooltipVisibility]);*/

    /** Setup wizard **/
    const userId = user?._id;

    const updateSetupProgress = useMutation({
        mutationFn: async (setupProgress) => {
            const response = await axios.put(`${baseURL}/api/users/setupProgress`, 
                { setupProgress });
            return response.data.setupProgress;
        },
        onMutate: async (newProgress) => {
            await queryClient.cancelQueries(["user", userId]);
        
            const previousUser = queryClient.getQueryData(["user", userId]);
        
            useAppStore.setState({
                selectedWords: newProgress.selectedWords,
                selectedLayout: newProgress.selectedLayout,
                selectedShape: newProgress.selectedShape,
                selectedSpacing: newProgress.selectedSpacing,
            });
        
            queryClient.setQueryData(["user", userId], (old) => ({
                ...old,
                setupProgress: newProgress,
            }));
        
            return { previousUser };
            },
        onError: (_err, _vars, context) => {
            if (context?.previousUser) {
                queryClient.setQueryData(["user", userId], context.previousUser);
            }
        
            showNotification({
                notification: t("setupWizard.errors.setupProgressSaveFailed"),
                type: "error",
                extra: {
                message: "Failed to update setup progress on the server",
                userId,
                },
                isException: true,
            });
        },
        onSuccess: (data) => {
            useAppStore.setState({
                selectedWords: data.selectedWords,
                selectedLayout: data.selectedLayout,
                selectedShape: data.selectedShape,
                selectedSpacing: data.selectedSpacing,
            });
        
            queryClient.setQueryData(["user", userId], (old) => ({
                ...old,
                setupProgress: data,
            }));
        },
    
        onSettled: () => {
            queryClient.invalidateQueries(["user", userId]);
        },
    });

    const completeSetupWizard = useMutation({
        mutationFn: async () => {
          return axios.post(`${baseURL}/api/users/completeSetupWizard`);
        },
      
        onSuccess: () => {
          queryClient.invalidateQueries(['user']);
        },
      
        onError: (error) => {
          showNotification({
            notification: t('setupWizard.errors.setupCompleteFailed'),
            type: 'error',
            extra: {
              message: 'Failed to update setup complete on the server',
              userId,
            },
            isException: true,
          });
        },
      });

      const skipSetupWizard = useMutation({
        mutationFn: async () => {
          return axios.post(`${baseURL}/api/users/skipSetupWizard`);
        },
      
        onSuccess: () => {
          queryClient.invalidateQueries(['user']);
        },
      
        onError: (error) => {
          showNotification({
            notification: t("setupWizard.errors.skipFailed"),
            type: "error",
            extra: {
              message: "Failed to skip the setup wizard",
              userId,
            },
            isException: true,
          });
        },
      });

    return {
        wordList, 
        isWordListPending,
        isWordListError, 
        wordListRefetch,
        updateWordList,
        updateWordListOnReorder,
        addGroup,
        deleteGroup,
        addWordItem,
        deleteWord,
        soundboard,
        shapes,
        isSoundboardPending,
        isSoundboardError, 
        soundboardRefetch,
        addShape,
        updateShape,
        updateButton,
        unassignButton,
        deleteShape,
        handleOptionChange,
        handleAssignedWordTooltipVisibility,
        assignedWordTooltipVisibility,
        updateSetupProgress,
        completeSetupWizard,
        skipSetupWizard
      };
};